/* eslint-disable react/jsx-props-no-spreading */
import { ComponentType, useEffect, useRef, useState } from 'react';
import Popover from '@mui/material/Popover';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import moment, { Moment } from 'moment';
import './dateRangePicker.scss';

export interface DateRangeValue {
  startDate?: Date;
  endDate?: Date;
}

export interface DateRangePickerProps {
  className?: string;
  startDate?: Date;
  endDate?: Date;
  onChange: (range: DateRangeValue) => void;
  disabled?: boolean;
  disabledFuture?: boolean;
  startLabel?: string;
  endLabel?: string;
}

const DATE_FORMAT = 'MM/DD/YYYY';

interface RangeDayOwnerProps {
  rangeStart: Moment | null;
  rangeEnd: Moment | null;
}

// Highlights the shared start/end selection across both calendars. The band
// is drawn on a wrapper element around each day (not a pseudo-element of the
// day button itself), because a box's own background always paints below its
// own ::before/::after regardless of z-index — wrapping lets the band sit
// behind the button so it can underlap the solid selected-day circle.
function RangeDay(props: PickersDayProps<Moment> & RangeDayOwnerProps) {
  const { rangeStart, rangeEnd, day, className, ...other } = props;
  const isRangeStart = !!rangeStart && day.isSame(rangeStart, 'day');
  const isRangeEnd = !!rangeEnd && day.isSame(rangeEnd, 'day');
  const isBoundary = isRangeStart || isRangeEnd;
  const inRange =
    !!rangeStart &&
    !!rangeEnd &&
    day.isAfter(rangeStart, 'day') &&
    day.isBefore(rangeEnd, 'day');
  const inBand = isBoundary || inRange;
  // moment's day() is locale-relative (0 = first day of the week), matching
  // the first/last visible column of each calendar row.
  const isRowStart = day.day() === 0;
  const isRowEnd = day.day() === 6;
  const roundLeft = inBand && (isRowStart || isRangeStart);
  const roundRight = inBand && (isRowEnd || isRangeEnd);

  return (
    <span
      className={[
        'mui-date-range-picker__day-cell',
        inBand ? 'mui-date-range-picker__day-cell--in-band' : '',
        roundLeft ? 'mui-date-range-picker__day-cell--round-left' : '',
        roundRight ? 'mui-date-range-picker__day-cell--round-right' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <PickersDay
        {...other}
        day={day}
        selected={isBoundary}
        className={[
          className,
          other.today ? 'mui-date-range-picker__day--today' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      />
    </span>
  );
}

export default function DateRangePicker({
  className,
  startDate,
  endDate,
  onChange,
  disabled = false,
  disabledFuture = false,
  startLabel = 'Start Date',
  endLabel = 'End Date',
}: DateRangePickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // Calendar values are kept in local state so a single (start-only) pick
  // stays visible even before onChange has a complete, committable range.
  const [workingStart, setWorkingStart] = useState<Moment | null>(
    startDate ? moment(startDate) : null,
  );
  const [workingEnd, setWorkingEnd] = useState<Moment | null>(
    endDate ? moment(endDate) : null,
  );

  useEffect(() => {
    setWorkingStart(startDate ? moment(startDate) : null);
    setWorkingEnd(endDate ? moment(endDate) : null);
  }, [startDate, endDate]);

  const commitChange = (nextStart: Moment | null, nextEnd: Moment | null) => {
    if (!nextStart?.isValid() || !nextEnd?.isValid()) {
      return;
    }
    onChange({ startDate: nextStart.toDate(), endDate: nextEnd.toDate() });
  };

  const handleStartChange = (value: Moment | null) => {
    setWorkingStart(value);
    commitChange(value, workingEnd);
  };

  const handleEndChange = (value: Moment | null) => {
    setWorkingEnd(value);
    commitChange(workingStart, value);
    setOpen(false);
  };

  const dayProps = { rangeStart: workingStart, rangeEnd: workingEnd };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div
        ref={containerRef}
        className={`mui-date-range-picker ${className ?? ''}`}
      >
        <div className="mui-date-range-picker__field">
          <label htmlFor="date-range-picker-start">{startLabel}</label>
          <button
            type="button"
            id="date-range-picker-start"
            className="mui-date-range-picker__input"
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <span>
              {workingStart ? workingStart.format(DATE_FORMAT) : 'mm/dd/yyyy'}
            </span>
            <span className="mui-date-range-picker__icon" aria-hidden="true" />
          </button>
          <span className="mui-date-range-picker__hint">mm/dd/yyyy</span>
        </div>
        <div className="mui-date-range-picker__field">
          <label htmlFor="date-range-picker-end">{endLabel}</label>
          <button
            type="button"
            id="date-range-picker-end"
            className="mui-date-range-picker__input"
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <span>
              {workingEnd ? workingEnd.format(DATE_FORMAT) : 'mm/dd/yyyy'}
            </span>
            <span className="mui-date-range-picker__icon" aria-hidden="true" />
          </button>
          <span className="mui-date-range-picker__hint">mm/dd/yyyy</span>
        </div>
      </div>
      <Popover
        open={open}
        anchorEl={containerRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        className="mui-date-range-picker__popover"
      >
        <div className="mui-date-range-picker__calendars">
          <DateCalendar
            value={workingStart}
            onChange={handleStartChange}
            disableFuture={disabledFuture}
            maxDate={workingEnd ?? undefined}
            slots={{ day: RangeDay as ComponentType<PickersDayProps<Moment>> }}
            slotProps={{ day: dayProps as never }}
          />
          <div className="mui-date-range-picker__divider" />
          <DateCalendar
            value={workingEnd}
            onChange={handleEndChange}
            disableFuture={disabledFuture}
            minDate={workingStart ?? undefined}
            slots={{ day: RangeDay as ComponentType<PickersDayProps<Moment>> }}
            slotProps={{ day: dayProps as never }}
          />
        </div>
      </Popover>
    </LocalizationProvider>
  );
}
